import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

function FileManager({ config, user }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // Memoize showMessage to be used in other callbacks
  const showMessage = useCallback((text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(''), 3000);
  }, []);

  const loadFiles = useCallback(async () => {
    if (!user?.email) return;
    
    setLoading(true);
    try {
      const response = await axios.get('/api/files/list', {
        headers: { 'X-User-Email': user.email }
      });
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Error loading files:', error);
      showMessage('Failed to load files', 'error');
    } finally {
      setLoading(false);
    }
  }, [user?.email, showMessage]);

  useEffect(() => {
    if (user?.email) {
      loadFiles();
    }
  }, [loadFiles, user?.email]);

  const handleFileUpload = async (e) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (!file) return;

    // Validate file type
    const allowedTypes = config?.allowedFileTypes || '.pdf';
    const allowedExtensions = allowedTypes.split(',').map(ext => ext.trim().toLowerCase());
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExtension)) {
      showMessage(`File type not allowed. Accepted formats: ${allowedTypes}`, 'error');
      e.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      await axios.post('/api/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-User-Email': user?.email
        }
      });
      showMessage('File uploaded successfully', 'success');
      // Delay refresh to allow n8n workflow to complete processing
      setTimeout(() => {
        loadFiles();
      }, 3000);
    } catch (error) {
      console.error('Error uploading file:', error);
      showMessage('Failed to upload file', 'error');
    } finally {
      setUploading(false);
      // Reset input if it exists
      if (e.target.value) e.target.value = '';
    }
  };

  const handleDeleteFile = async (fileId, fileName) => {
    if (!window.confirm(`Delete ${fileName}?`)) return;

    try {
      await axios.delete(`/api/files/${encodeURIComponent(fileId)}`, {
        headers: { 'X-User-Email': user?.email },
        data: { fileName }
      });
      showMessage('File deleted successfully', 'success');
      loadFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      showMessage('Failed to delete file', 'error');
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const response = await axios.get(`/api/files/download/${encodeURIComponent(fileId)}`, {
        responseType: 'blob',
        headers: { 'X-User-Email': user?.email },
        params: { fileName }
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showMessage('File downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading file:', error);
      showMessage('Failed to download file', 'error');
    }
  };

  const handleUpdateVectorStore = async () => {
    setUpdating(true);
    try {
      await axios.post('/api/files/retrain');
      showMessage('Vector store update triggered successfully', 'success');
    } catch (error) {
      console.error('Error updating vector store:', error);
      showMessage('Failed to update vector store', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = config?.allowedFileTypes || '.pdf';
    const allowedExtensions = allowedTypes.split(',').map(ext => ext.trim().toLowerCase());
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExtension)) {
      showMessage(`File type not allowed. Accepted formats: ${allowedTypes}`, 'error');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      await axios.post('/api/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-User-Email': user?.email
        }
      });
      showMessage('File uploaded successfully', 'success');
      // Delay refresh to allow n8n workflow to complete processing
      setTimeout(() => {
        loadFiles();
      }, 3000);
    } catch (error) {
      console.error('Error uploading file:', error);
      showMessage('Failed to upload file', 'error');
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (mimeType) => {
    const type = (mimeType || '').toLowerCase();
    if (type.includes('pdf')) return 'picture_as_pdf';
    if (type.includes('sheet') || type.includes('excel')) return 'table_view';
    if (type.includes('presentation') || type.includes('powerpoint')) return 'slideshow';
    if (type.includes('document') || type.includes('word')) return 'description';
    if (type.includes('text')) return 'description';
    return 'insert_drive_file';
  };

  const getFileIconColor = (mimeType) => {
    const type = (mimeType || '').toLowerCase();
    if (type.includes('pdf')) return 'bg-red-50 text-red-500 border-red-100';
    if (type.includes('sheet') || type.includes('excel')) return 'bg-green-50 text-green-600 border-green-100';
    if (type.includes('presentation') || type.includes('powerpoint')) return 'bg-orange-50 text-orange-500 border-orange-100';
    return 'bg-blue-50 text-primary border-blue-100';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    // If bytes comes as string from backend, try to parse
    const numBytes = Number(bytes);
    if (isNaN(numBytes)) return bytes; // Return as is if not a number

    const kb = numBytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `Uploaded ${minutes} mins ago`;
    if (hours < 24) return `Uploaded ${hours} hours ago`;
    if (days === 1) return 'Uploaded yesterday';
    if (days < 30) return `Uploaded ${days} days ago`;
    return `Uploaded ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };


  return (
    <div className="flex-1 flex flex-col bg-white overflow-hidden h-full">
      {/* Header - Hidden on mobile */}
      <header className="hidden lg:flex items-center justify-between px-12 py-8 border-b border-gray-100 bg-white sticky top-0 z-10 shrink-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">AI Agent Knowledge Base</h1>
          <p className="text-gray-500 mt-1">Manage documents and data sources for your AI assistant.</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleUpdateVectorStore}
            disabled={updating || files.length === 0}
            className="px-6 py-2.5 bg-primary hover:bg-primary-dark text-white rounded-input font-semibold shadow-sm transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[20px]">{updating ? 'sync' : 'psychology'}</span>
            {updating ? 'Re-training...' : 'Re-train'}
          </button>
        </div>
      </header>

      {/* Mobile Action Bar */}
      <div className="lg:hidden px-4 py-3 bg-white border-b border-gray-100 flex items-center gap-3 shrink-0">
        <button
          onClick={handleUpdateVectorStore}
          disabled={updating || files.length === 0}
          className="flex-1 px-4 py-2.5 bg-primary hover:bg-primary-dark text-white rounded-xl font-semibold text-sm shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="material-symbols-outlined text-[18px]">{updating ? 'sync' : 'psychology'}</span>
          {updating ? 'Re-training...' : 'Re-train'}
        </button>
      </div>

      <main className="flex-1 overflow-y-auto px-4 py-4 lg:px-12 lg:py-10 bg-gray-50/30">
        <div className="max-w-6xl mx-auto space-y-10">
          {message && (
            <div className={`px-6 py-4 rounded-input ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'} flex items-center gap-3 shadow-sm`}>
              <span className="material-symbols-outlined">{message.type === 'success' ? 'check_circle' : 'error'}</span>
              <span className="font-medium">{message.text}</span>
            </div>
          )}

          <div 
            className={`group relative flex flex-col items-center justify-center w-full h-80 border-2 border-dashed rounded-input bg-white transition-all duration-300 cursor-pointer overflow-hidden ${
              isDragging 
                ? 'border-primary bg-blue-50/30 shadow-lg' 
                : 'border-gray-300 hover:bg-blue-50/20 hover:border-primary hover:shadow-lg'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              id="file-upload"
              type="file"
              onChange={handleFileUpload}
              disabled={uploading}
              accept={config?.allowedFileTypes || '.pdf'}
              className="hidden"
            />
            <label
              htmlFor="file-upload"
              className="absolute inset-0 cursor-pointer flex flex-col items-center justify-center"
            >
              <div className="relative z-10 flex flex-col items-center text-center p-6 pointer-events-none">
                <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-primary/20 transition-all duration-300 shadow-sm">
                  <span className="material-symbols-outlined text-[40px] text-primary">cloud_upload</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {uploading ? 'Uploading...' : 'Upload New Knowledge'}
                </h3>
                <p className="text-gray-500 mb-8 max-w-md mx-auto">
                  Drag & drop {(config?.allowedFileTypes || '.pdf').split(',').map(ext => ext.trim().toUpperCase().replace('.', '')).join(', ')} files here to instantly train your agent with new data.
                </p>
                {!uploading && (
                  <div className="px-8 py-3 bg-primary hover:bg-primary-dark text-white rounded-input font-semibold shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all flex items-center gap-2 pointer-events-auto">
                    <span className="material-symbols-outlined">add_circle</span>
                    Select Documents
                  </div>
                )}
              </div>
            </label>
          </div>

          <div className="space-y-5">
            <div className="flex items-center justify-between px-2">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">folder_copy</span>
                Current Knowledge Base Files ({files.length})
              </h2>
              <button
                onClick={loadFiles}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold text-sm shadow-sm transition-all flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-[18px]">refresh</span>
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20 bg-white rounded-input border border-gray-200">
                <div className="flex flex-col items-center gap-3">
                  <span className="material-symbols-outlined text-primary text-[40px] animate-spin">sync</span>
                  <span className="text-gray-600 font-medium">Loading files...</span>
                </div>
              </div>
            ) : files.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 bg-white rounded-input border border-gray-200">
                <span className="material-symbols-outlined text-gray-300 text-[80px] mb-4">folder_open</span>
                <p className="text-gray-600 font-medium mb-2">No files in Knowledge Base</p>
                <p className="text-gray-400 text-sm">Upload a file to get started</p>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-input shadow-sm overflow-hidden mb-12">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/80 border-b border-gray-100 text-xs uppercase text-gray-500 font-semibold tracking-wide">
                      <th className="px-8 py-5 font-medium w-2/5">File Name</th>
                      <th className="px-6 py-5 font-medium">Type</th>
                      <th className="px-6 py-5 font-medium text-right">Size</th>
                      <th className="px-6 py-5 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {files.map((file) => (
                      <tr key={file.id} className="group hover:bg-blue-50/30 transition-colors">
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-4">
                            <div className={`w-11 h-11 rounded-xl ${getFileIconColor(file.mimeType)} border flex items-center justify-center shrink-0 shadow-sm group-hover:scale-105 transition-transform`}>
                              <span className="material-symbols-outlined">{getFileIcon(file.mimeType)}</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-[15px] font-semibold text-gray-900 group-hover:text-primary transition-colors">
                                {file.name}
                              </span>
                              <span className="text-xs text-gray-400">{formatDate(file.createdTime)}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                            {file.mimeType.split('/').pop().toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-5 text-sm text-gray-500 text-right font-medium">
                          {formatFileSize(file.size)}
                        </td>
                        <td className="px-6 py-5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleDownloadFile(file.id, file.name)}
                              className="w-9 h-9 inline-flex items-center justify-center rounded-lg hover:bg-blue-50 text-gray-400 hover:text-primary transition-colors"
                              title="Download"
                            >
                              <span className="material-symbols-outlined text-[20px]">download</span>
                            </button>
                            <button
                              onClick={() => handleDeleteFile(file.id, file.name)}
                              className="w-9 h-9 inline-flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                              title="Delete"
                            >
                              <span className="material-symbols-outlined text-[20px]">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default FileManager;
